import { createAction, createAsyncThunk } from "@reduxjs/toolkit";
import { apiSearchActors } from "../async/searchAsync";
import { apiGetActorById, apiMergeActors } from "../async/actorAsync";
import { generateSearchRequest } from "../../utility/search";
import { ActorSearchRequest, ActorSearchResult, defaultSearchFields } from "../model/searchModel";
import { Actor, GetActorByIdRequest, GetActorByIdResponse } from "../model/actorModel";
import { MergeActorsCommand } from "../model/actorMergeModel";

export type ActorMergeSearchKey = "target" | "source";

export interface ActorMergeSearchActorsRequest {
    key: ActorMergeSearchKey;
    searchText: string;
    limit?: number;
}

export const clearActorMergeSearch = createAction(
    "actorMerge/CLEAR_SEARCH",
    (payload: { key: ActorMergeSearchKey }) => ({ payload })
);

export const searchActorMergeActors = createAsyncThunk<
    { key: ActorMergeSearchKey; results: ActorSearchResult[]; limit: number; searchText: string },
    ActorMergeSearchActorsRequest,
    { state: RootState; rejectValue: string }
>(
    "actorMerge/SEARCH_ACTORS",
    async (payload, { getState, rejectWithValue }) => {
        const token = getState().auth.user?.token ?? "";
        const limit = payload.limit ?? 5;
        const searchText = payload.searchText?.trim() ?? "";

        if (searchText.length === 0) {
            return { key: payload.key, results: [], limit, searchText };
        }

        const request = {
            ...generateSearchRequest(
                { ...defaultSearchFields, name: searchText },
                limit,
                0,
                false
            ),
            ShoeSizeFrom: undefined,
            ShoeSizeTo: undefined,
            ActTrakOnly: true,

        } as unknown as ActorSearchRequest;

        const response = await apiSearchActors(request, token, rejectWithValue);
        return {
            key: payload.key,
            results: (response?.data ?? []).slice(0, limit),
            limit,
            searchText
        };
    }
);

export const getActorMergeActorDetails = createAsyncThunk<
    Actor,
    GetActorByIdRequest,
    { state: RootState; rejectValue: string }
>(
    "actorMerge/GET_ACTOR_DETAILS",
    async (payload, { getState, rejectWithValue }) => {
        const token = getState().auth.user?.token ?? "";
        const response: GetActorByIdResponse = await apiGetActorById(payload, token, rejectWithValue);
        return response.actor;
    }
);

export const mergeActors = createAsyncThunk<
    void,
    MergeActorsCommand,
    { state: RootState; rejectValue: string }
>(
    "actorMerge/MERGE_ACTORS",
    async (payload, { getState, rejectWithValue }) => {
        const token = getState().auth.user?.token ?? "";
        return await apiMergeActors(payload, token, rejectWithValue);
    }
);
